// Persists the acquired Graph access token so the session survives task pane
// reopens and Outlook restarts, independent of the sign-in path (SSO or MSAL).
// This mirrors what MSAL already does (localStorage token cache) and is the
// standard approach for SPA-style clients. REQ-AUTH-005.

import { decodeJwtPayload } from "./jwt";
import { storageGet, storageRemove, storageSet } from "./safeStorage";

const STORAGE_KEY = "lox24.graphToken";
const EXPIRY_BUFFER_MS = 60_000; // treat as expired 60s early to avoid edge misses

interface CachedToken {
  token: string;
  exp: number; // seconds since epoch (from the JWT `exp` claim)
}

/** Reads the `exp` claim (seconds since epoch) from a JWT, or null if unreadable. */
function getJwtExpiry(jwt: string): number | null {
  const exp = decodeJwtPayload(jwt)?.exp;
  return typeof exp === "number" ? exp : null;
}

export function cacheToken(token: string): void {
  const exp = getJwtExpiry(token);
  if (exp) {
    storageSet(STORAGE_KEY, JSON.stringify({ token, exp } satisfies CachedToken));
  }
}

/** Returns the cached token if present and not near expiry, otherwise null. */
export function getCachedToken(): string | null {
  const raw = storageGet(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const { token, exp } = JSON.parse(raw) as CachedToken;
    if (exp * 1000 > Date.now() + EXPIRY_BUFFER_MS) {
      return token;
    }
  } catch {
    // fall through to clear
  }
  storageRemove(STORAGE_KEY);
  return null;
}

export function clearCachedToken(): void {
  storageRemove(STORAGE_KEY);
}
